import React, {
  forwardRef,
  Ref,
  useMemo,
  useState,
} from 'react';

import { MergeElementProps } from '@/core/interfaces/common';
import { useGlobalContext } from '@/core/contexts/GlobalContext/GlobalContext';

import { theme } from '@/themes/main';

import {
  StyledIconButton,
} from './IconButton.styles';
import {
  ButtonVariant,
} from '../Button/Button.types';
import { getIconColor } from '../Button/Button.helpers';
import {
  CustomIconButtonProps,
  IconButtonSizes,
} from './IconButton.types';

export type IconButtonProps = MergeElementProps<
  'button',
  CustomIconButtonProps
>

export const IconButton = forwardRef(({
  type = 'button',
  onClick,
  iconColor,
  variant = ButtonVariant.PRIMARY,
  isDisabled,
  icon,
  renderAs,
  size = IconButtonSizes.REGULAR,
  ariaLabel,
  ...props
}: IconButtonProps, ref: Ref<HTMLButtonElement>) => {
  const { isTouchDevice } = useGlobalContext();

  const [
    isHover,
    setIsHover,
  ] = useState(false);

  const iconSizes = {
    [IconButtonSizes.SMALL]: 24,
    [IconButtonSizes.REGULAR]: 32,
    [IconButtonSizes.HUGE]: 32,
  };

  const iconColorType = useMemo(
    () => getIconColor({
      isDisabled,
      isHover,
      isIconButton: true,
      theme,
      variant,
    }),
    [
      variant,
      isHover,
      isDisabled,
    ]
  );

  return (
    <StyledIconButton
      ref={ref}
      type={type}
      onClick={onClick}
      variant={variant}
      disabled={isDisabled}
      onMouseEnter={() => setIsHover(!isTouchDevice)}
      onMouseLeave={() => setIsHover(false)}
      size={size}
      as={renderAs as any}
      aria-label={ariaLabel}
      {...props}
    >
      { React.cloneElement(icon, {
        fill: (iconColorType),
        size: iconSizes[size as IconButtonSizes],
      }) }
    </StyledIconButton>
  );
});
